<template>
  <div class="quick-cart" v-click-outside="showEvent">
    <!-- Desktop view -->
    <template v-if="!isMobile">
      <div class="cart-view" v-if="isQuickCartHydrated && quickCartItems.length">
        <header class="header">
          <div class="heading">{{ $t('shopping.quickcart.basket') }}</div>
          <button type="button" class="header-close iconfont-cross" aria-label="Close"></button>
        </header>
        <div class="content">
          <div class="content-header">
            <div class="heading">{{ $t('common.overview') }}</div>
            <a href="#" class="link" @click.prevent="handleRemoveAll">{{ $t('app.cart.clear_all') }}</a>
          </div>
          <CartItem
              :item="item"
              v-for="item in quickCartItems"
              :key="item.id || item.product?.id"
              :on-delete-item="deleteCartItem"
          />
        </div>
        <footer class="footer">
          <PurchaseSummary />
          <button
              type="submit"
              name="submit"
              class="b b-A"
              @click="goToCheckout"
          >
            {{ $t('shopping.basket.to_checkout') }}
          </button>
          <PaymentIcons />
          <ul class="payment-buttons">
            <div ref="adyenContainer" class="express-payment-wrapper"></div>
          </ul>
        </footer>
      </div>
      <div v-else-if="!isQuickCartHydrated" class="cart-view cart-view--loading">
        <header class="header">
          <div class="heading">{{ $t('shopping.quickcart.basket') }}</div>
          <button type="button" class="header-close iconfont-cross" aria-label="Close"></button>
        </header>
        <div class="content cart-view__loading-content">
          <div class="pp-in wait"></div>
        </div>
      </div>
      <div v-else class="cart-view">
        <header class="header">
          <div class="heading">{{ $t('shopping.quickcart.basket') }}</div>
          <button type="button" class="header-close iconfont-cross" aria-label="Close"></button>
        </header>
        <div class="content">
          <div class="empty-state">
            <div class="heading">{{ $t('shopping.basket_empty.empty_headline') }}</div>
            <div class="iconfont-cart-outline icon"></div>
          </div>
        </div>
      </div>
      <div class="pp-in wait" v-if="pending && isQuickCartHydrated"></div>
    </template>

    <!-- Mobile view – uses Checkout Mobile view (router-view with Cart, Checkout, etc.) -->
    <template v-else>
      <MobileView />
    </template>
  </div>
  <NBConfirmModal
      v-model="showConfirm"
      @confirm="removeAllItems"
      :title="$t('app.cart.headline')"
      :message="$t('app.cart.description')"
      :confirmText="$t('app.cart.button_text')"
  />
  <FreeSamplesModal
      v-if="isFreeSamplesModalVisible"
      :key="freeSamplesModalKey"
      :model-value="true"
      @update:model-value="onFreeSamplesModalUpdate"
  />
  <VoucherModal
      v-if="isVoucherModalVisible"
      :model-value="true"
      @update:model-value="onVoucherModalUpdate"
  />
</template>
<style lang="scss" src="./styles.scss" scoped></style>

<script>
/* QuickCart loaded */
import { mapActions, mapState } from 'vuex'
import { mapFields } from 'vuex-map-fields'

import { useBreakpoint } from '@/app/Checkout/composables/useBreakpoint'
import { trackScreenView } from '@/helper'

import CartItem from '@/components/QuickCart/CartItem/index.vue'
import PaymentIcons from '@/components/PaymentIcons/index.vue'
import PurchaseSummary from '@/components/modules/PurchaseSummary/index.vue'
import NBConfirmModal from '@/app/Checkout/components/common/NBConfirmModal/index.vue'
import FreeSamplesModal from '@/app/Checkout/components/FreeSamplesModal/index.vue'
import VoucherModal from '@/app/Checkout/components/VoucherModal/index.vue'
import MobileView from '@/app/Checkout/views/Mobile/index.vue'

export default {
  name: 'QuickCart',
  setup() {
    const { isMobile } = useBreakpoint()
    return { isMobile }
  },
  provide() {
    return {
      isOverlay: true,
    }
  },
  components: {
    CartItem,
    NBConfirmModal,
    FreeSamplesModal,
    VoucherModal,
    MobileView,
    PurchaseSummary,
    PaymentIcons,
  },
  data() {
    return {
      error: false,
      timer: null,
      showConfirm: false,
      /** After first getCart() completes; do not use `cart != null` (API may leave cart null when empty). */
      quickCartFetchSettled: false,
    }
  },
  computed: {
    ...mapState('cart', [
      'cart',
      'pending',
    ]),
    ...mapState('settings', ['settings']),
    ...mapState('auth', ['isAuthenticated']),
    ...mapFields('cart', [
      'quickCartVisible',
      'min_order_value',
    ]),
    quickCartItems() {
      const c = this.cart
      if (!c) return []
      return c.cart_items ?? c.cart?.cart_items ?? []
    },
    isQuickCartHydrated() {
      return this.quickCartFetchSettled
    },
    isFreeSamplesModalVisible() {
      return Boolean(this.$store.state.product?.isFreeSamplesModalVisible)
    },
    freeSamplesModalKey() {
      return this.$store.state.product?.freeSamplesModalKey ?? 0
    },
    isVoucherModalVisible() {
      const v = this.$store.state.vouchers
      return Boolean(v && v.isVoucherModalVisible)
    },
  },
  watch: {
    cart: {
      handler(newCart) {
        // updateSelectedSamplesFromCart handled by Checkout's cart watcher (shared store)
        const itemCount =
          (newCart?.cart_items?.length ?? newCart?.cart?.cart_items?.length ?? 0)
        if (itemCount === 0) {
          this.$store.dispatch('product/incrementFreeSamplesModalKey')
        }
      },
      immediate: true,
    },
    async quickCartVisible(val) {
      if (val) {
        this.toggleHtmlClass('no-scrl', 'addClass')
        this.toggleBodyClass('has-mdl', 'addClass')
        try {
          await this.getCart()
        } finally {
          this.quickCartFetchSettled = true
        }
        trackScreenView('Cart', {}, 'cart_summary')
        this.getCartVouchers()
        this.$store.dispatch('product/getFreeSamples')
        if (document.querySelector('.a-A.prod.on') !== null) {
          document.querySelector('.a-A.prod.on').classList && document.querySelector('.a-A.prod.on').classList.remove('on')
        }
        // push event
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          'ecommerce': {
            'pageType': 'Cart view event',
          },
        })
      } else {
        this.toggleHtmlClass('no-scrl')
        this.toggleBodyClass('has-mdl')
      }
    },
    summary(val) {
      const cart = { 'cart': { 'props': { 'count': val.props.count } } }
      // eslint-disable-next-line no-undef
      $.dataPopulate(cart)
      const quickcartCounter = document.querySelectorAll('.cart-count')
      if (quickcartCounter.length && !parseInt(quickcartCounter[0].innerHTML)) {
        quickcartCounter.forEach(elem => elem.classList.add('empty'))
      }
    },
    pending: 'checkLoadingStatus',
    quickCartFetchSettled: 'checkLoadingStatus',
  },
  mounted() {
    // Use document-level capture so we catch clicks even if host overlay blocks our $el
    this._closeHandler = (e) => {
      const btn = e.target?.closest?.('.quick-cart .header-close')
      if (btn) {
        e.stopPropagation()
        e.preventDefault()
        this.closeCart() // async: navigates to Cart if on Checkout, then closes
      }
    }
    document.addEventListener('click', this._closeHandler, true)

    const markCartFetchDone = () => {
      this.quickCartFetchSettled = true
    }
    if (this.cart != null) {
      markCartFetchDone()
    } else {
      Promise.resolve(this.getCart()).then(markCartFetchDone, markCartFetchDone)
    }

    this.getCartVouchers()
    this.getCardStyles()
    this.getAuthStatus()
    this.$store.dispatch('user/getAddresses')
    this.$store.dispatch('country/getCountries')
    this.$store.dispatch('user/getCountryPhoneCodes')
    if (!this.$store.state.checkout?.checkout && !this.$store.state.checkout?.pending) {
      this.$store.dispatch('checkout/getCheckout')
    }
    trackScreenView('Checkout', {}, 'cart_summary');
  },
  updated() {
    if (this.$refs.adyenContainer && !this.buttonsInitialized) {
      this.buttonsInitialized = true
      this.loadExpressButtons(this.$refs.adyenContainer)
    }
  },
  methods: {
    ...mapActions('cart', [
      'getCart',
      'removeAllItems',
      'removeProduct',
      'removeBeautyDeal',
      'removeWishlistProduct',
    ]),
    ...mapActions('vouchers', ['getCartVouchers']),
    ...mapActions('eVouchers', ['getCardStyles']),
    ...mapActions('auth', ['getAuthStatus']),
    checkLoadingStatus() {
      if (!this.pending && this.isQuickCartHydrated && !this.isMobile) {
        setTimeout(() => {
          if (window.cartOpenTimeout === true) {
            window.cartOpenTimeout = false
            this.closeCart()
          }
        }, 3500)
      }
    },
    async loadExpressButtons(element) {
      if (!element) return
      element.innerHTML = ''

      try {
        const $ = window.$ || window.jQuery || {}
        const response = await fetch('/de-de/home/overlay/express_payment')
        const html = await response.text()
        const $parser = $(html)
        const $paymentSection = $parser.filter('#direct-pay-section-payment-options')

        if ($paymentSection.length) {
          element.innerHTML = $paymentSection[0].outerHTML

          const scripts = element.querySelectorAll('script')
          scripts.forEach((oldScript) => {
            const newScript = document.createElement('script')
            Array.from(oldScript.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value))
            newScript.textContent = oldScript.textContent
            oldScript.parentNode.replaceChild(newScript, oldScript)
          })

          setTimeout(() => {
            if (window.nbInitPaymentButtons) {
              window.nbInitPaymentButtons($(element))
            }
          }, 500)
        }
      } catch (err) {
        console.error('Failed to load express payment buttons', err)
      }
    },
    async deleteCartItem(item) {
      try {
        if (item.deal_type) {
          await this.removeBeautyDeal(item.id)
        } else if (item.wishlist_name && item.datafield?.wishlist_id) {
          await this.removeWishlistProduct({
            productId: item.product.id,
            wishlistId: item.datafield.wishlist_id,
          })
        } else {
          await this.removeProduct(item.product.id)
        }
        await this.getCart()
        const cart = this.$store.state.cart?.cart
        if (cart) {
          await this.$store.dispatch('product/updateSelectedSamplesFromCart', cart)
        }
      } catch (err) {
        throw err
      } finally {
        await this.getCartVouchers()
      }
    },
    handleRemoveAll() {
      this.showConfirm = true
    },
    async goToCheckout() {
      sessionStorage.setItem('pageUrl', window.location.href)

      await this.getAuthStatus()
      if (!this.isAuthenticated) {
        const event = new CustomEvent('quickcart:login-required', {
          bubbles: true,
          cancelable: true,
          detail: { source: 'quickcart', action: 'checkout' },
        })
        window.dispatchEvent(event)
        document.dispatchEvent(event)
        this.quickCartVisible = false
        return
      }
      const locale = this.$i18n.locale ? this.$i18n.locale.toLowerCase() : `${window.config.lang || 'de'}-${window.config.country || 'de'}`
      document.location.href = `/${locale}/checkout/purchase`
    },
    showEvent(event) {
      if (event.target.className.includes('quickcart-opener')) {
        this.quickCartVisible = true
      }
    },
    async closeCart() {
      // On mobile: if we're on Checkout, navigate to Cart first so user sees Cart when they reopen
      if (this.isMobile && this.$router?.currentRoute?.value?.name === 'Checkout') {
        await this.$router.replace({ name: 'Cart' })
        await this.$nextTick()
      }
      this.quickCartVisible = false
      document.dispatchEvent(new CustomEvent('quickcart:close', { bubbles: true }))
    },
    onFreeSamplesModalUpdate(visible) {
      if (!visible) {
        this.$store.commit('product/SET_FREE_SAMPLES_MODAL_VISIBLE', false)
      }
    },
    onVoucherModalUpdate(visible) {
      if (!visible) {
        this.$store.commit('vouchers/SET_VOUCHER_MODAL_VISIBLE', false)
        this.$store.commit('vouchers/REMOVE_ERROR')
      }
    },
  },
  beforeUnmount() {
    if (this._closeHandler) {
      document.removeEventListener('click', this._closeHandler, true)
    }
  },
}
</script>
